import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { useAuthStore } from 'src/store/auth';
import { useGeneralStore } from 'src/store';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useIsW2Mode } from '@features/w2/store/w2-mode';
import { W2HeaderLogout } from '@features/w2/components/W2HeaderLogout';

import Icon from '@components/shared/icon/Icon';
import { PoweredByOneHealth } from '@components/shared/PoweredByOneHealth';

import { cn, isLollySkin, shouldShowPoweredBy } from '@utils/index';

import { HeaderMenu } from './HeaderMenu';

interface HeaderProps {
  /**
   * When set, render the "Total cost per period" figure from this value
   * (ignoring the B2C `selectedStep`/`usePlan` gates). Used by the W2
   * `/select-plans` flow to surface the server quote total. Additive + default-off
   * so every existing `<Header />` mount is unchanged.
   */
  priceOverride?: number | null;
  /**
   * Opt-in for authenticated W2-owned surfaces. Default-off keeps every B2C
   * header unchanged; W2 shells opt in to the permanent employee logout action.
   */
  showW2Logout?: boolean;
}

export const Header = ({
  priceOverride,
  showW2Logout = false,
}: HeaderProps = {}) => {
  const { selectedStep, formSteps, handleJumpTo } = useFormControls();
  const { totalPlanPrice } = usePlan();
  const { siteConfig } = useGeneralStore();
  const { user } = useAuthStore();
  // W2 employee flow reuses the branded B2C header for the OneHealth logo, but as
  // a logo-only bar: no user menu / onboarding chrome, and the logo is inert (the
  // reduced token flow has no /form or /registration to navigate to).
  const isW2 = useIsW2Mode();
  const { data, errorCount } = useOnboardingStore();
  const formOnbording = useForm<OnboardingSchema>();

  const primaryApplicant = data.applicants?.[0];
  const displayName =
    primaryApplicant?.firstName || user?.firstName || user?.username || 'user';

  const location = useLocation();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const hideUserInfo = useMemo(() => {
    // W2 flow renders a logo-only header (no user menu / plan price / step nav).
    if (isW2) return true;
    const hiddenPaths = [
      '/login',
      '/registration',
      '/forgot-password',
      '/reset-password',
    ];
    return hiddenPaths.includes(location.pathname);
  }, [isW2, location.pathname]);

  const onLogoClick = () => {
    // W2 flow: the logo is branding-only; stay in the tokenized flow.
    if (isW2) return;

    const welcomeStepExists = formSteps.some(
      (step) => step.sectionName === 'Welcome'
    );
    const currentStep = formSteps[selectedStep];
    const isFinishUp = currentStep?.sectionName === 'Finish Up';

    if (
      isFinishUp ||
      location.pathname === '/thanks' ||
      location.pathname === '/healthhub'
    ) {
      window.location.href = window.location.origin + '/form';
      return;
    }

    if (!welcomeStepExists) {
      window.location.href = '/registration';
      return;
    }

    window.location.href = '/form';
  };

  const isHealthhub = location.pathname === '/healthhub';

  const logoSrc = isHealthhub ? siteConfig?.images?.logo : siteConfig?.logo;

  const hasSecondLogo =
    !!siteConfig?.logo &&
    shouldShowPoweredBy(siteConfig?.groupType, siteConfig?.webClientType) &&
    !isLollySkin();

  return (
    <div
      className={cn(
        'w-full z-40 left-0 flex flex-col gap-y-2 relative',
        !hideUserInfo &&
          'max-xl:fixed max-xl:bg-background max-xl:top-0 max-xl:py-4',
        !hideUserInfo && isLollySkin() && 'max-xl:bg-transparent'
      )}
      style={{ zIndex: 9997 }}
    >
      <div className="max-w-[1920px] relative mx-auto px-6 w-full max-lg:px-4.5">
        <div className="h-22 flex relative justify-between z-10 bg-white items-center rounded-full pl-11 pr-10 max-lg:pl-3 max-lg:pr-3 max-lg:h-13.5 max-xl:h-18">
          {!!errorCount && (
            <div
              id="Oops"
              className="absolute flex bg-error font-extrabold justify-between items-center px-6 gap-3 h-full w-full left-0 z-20 text-white rounded-xl"
            >
              <div className="flex items-center gap-3 2.5xl:w-[55%]">
                <img
                  src="/icons/error-big.svg"
                  alt="error icon"
                  className="w-[30px] lg:w-[45px] h-auto"
                />
                <span className="text-sm md:text-base lg:text-lg">
                  {errorCount > 1
                    ? 'Oops! We spotted some errors. Update the highlighted fields and try again.'
                    : 'Oops! We spotted an error. Update the highlighted field and try again.'}
                </span>
              </div>
              {/* <span className="w-[45%] hidden 2.5xl:flex text-lg text-nowrap">{errorCount > 1 ? 'Oops! We spotted some errors. Update the highlighted fields and try again.' : 'Oops! We spotted an error. Update the highlighted field and try again.'}</span> */}
            </div>
          )}
          <div
            onClick={onLogoClick}
            className="flex flex-1 gap-2 gap-y-4 items-center max-w-36 cursor-pointer select-none"
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onLogoClick();
            }}
          >
            <img
              src={logoSrc}
              className={cn(
                'w-36 h-auto max-w-38',
                hasSecondLogo
                  ? 'max-lg:max-w-29 max-xs:max-w-28'
                  : 'max-lg:max-w-23'
              )}
              alt="NGA"
            />
            {hasSecondLogo && (
              <PoweredByOneHealth className="ml-2 max-lg:ml-1" />
            )}
          </div>
          {!hideUserInfo ? (
            <div className="flex items-center gap-4 xl:gap-40">
              {priceOverride != null ? (
                // W2 /select-plans quote total (bypasses the B2C step gate).
                <p
                  data-testid="w2-select-header-total"
                  className="hidden sm:flex items-center gap-2 xl:text-2xl text-base"
                >
                  Total cost per period:
                  <span className="font-bold">
                    ${Number(priceOverride).toFixed(2)}
                  </span>
                </p>
              ) : (
                selectedStep > 1 &&
                totalPlanPrice > 0 &&
                location.pathname !== '/healthhub' && (
                  <p className="hidden sm:flex items-center gap-2 xl:text-2xl text-base">
                    Total cost per period:
                    <span className="font-bold">
                      ${Number(totalPlanPrice).toFixed(2)}
                    </span>
                  </p>
                )
              )}
              <div className="flex items-center gap-13 max-lg:gap-1">
                <p
                  data-testid="w2-select-header-name"
                  className={cn(
                    'flex gap-1 text-sm -mr-5 max-lg:hidden',
                    isLollySkin() ? 'text-[#1f0f03]' : 'text-[#002657]'
                  )}
                >
                  Hello
                  <strong className="">{displayName}</strong>
                </p>
                <div
                  className="relative flex items-center gap-13"
                  ref={dropdownRef}
                >
                  <strong
                    id="user-name"
                    className="flex rounded-full bg-primary text-white text-sm justify-center items-center w-9 h-9 max-lg:max-w-6.5 max-lg:max-h-6.5 max-lg:text-[10px] lg:pointer-events-none pointer-events-auto"
                    onClick={() => setDropdownOpen((v) => !v)}
                  >
                    {displayName?.slice(0, 2).toLocaleUpperCase() || 'JS'}
                  </strong>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((v) => !v)}
                    className="focus:outline-none max-lg:hidden"
                  >
                    <Icon
                      name="buter-icon"
                      className="flex size-5 shrink-0 text-primary"
                    />
                  </button>
                  {dropdownOpen && <HeaderMenu />}
                </div>
                {showW2Logout && <W2HeaderLogout />}
              </div>
            </div>
          ) : (
            showW2Logout && <W2HeaderLogout />
          )}
        </div>
      </div>
    </div>
  );
};
